import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import BlockIcon from '@material-ui/icons/Block';

import {
  getDowngradeApprovals,
  approveDowngrade,
  rejectDowngrade,
} from '../services/payments';

const ESTADOS = ['PENDIENTE', 'APROBADO', 'RECHAZADO', 'CANCELADO', 'all'];

const estadoColor = {
  PENDIENTE: '#ed6c02',
  APROBADO: '#d32f2f',
  RECHAZADO: '#2e7d32',
  CANCELADO: '#757575',
};

const useStyles = makeStyles(theme => ({
  table: {
    marginTop: 10,
    padding: 5,
    '& .MuiTableCell-sizeSmall': {
      padding: theme.spacing(0.75),
    },
  },
  header: {
    background: theme.palette.primary.main,
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  noResultsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: { minWidth: 160 },
  grow: { flex: 1 },
  motivoField: { marginTop: theme.spacing(2) },
}));

const formatDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function DowngradeApprovalsTable() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  const [filterEstado, setFilterEstado] = useState('PENDIENTE');
  const [filterEmail, setFilterEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');

  const [pendingAction, setPendingAction] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pageSizeOptions = [10, 25, 50, 100];

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  useEffect(() => {
    fetchData(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filterEstado, filterEmail, refreshToken]);

  const fetchData = async (pageNumber = 1, perPage = 25) => {
    setLoading(true);
    try {
      const filters = {};
      if (filterEstado) filters.estado = filterEstado;
      if (filterEmail) filters.email = filterEmail;
      const resp = await getDowngradeApprovals(pageNumber, perPage, filters);
      setRows(resp?.data?.results ?? []);
      setTotal(Number(resp?.data?.count) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = e => setPage(Number(e.target.value));
  const handleChangePageSize = e => { setPageSize(Number(e.target.value)); setPage(1); };
  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  const handleFilterEstado = e => { setFilterEstado(e.target.value); setPage(1); };
  const handleEmailSearch = () => { setFilterEmail(emailInput); setPage(1); };
  const handleEmailKeyDown = e => { if (e.key === 'Enter') handleEmailSearch(); };

  const openApprove = item => { setPendingAction({ type: 'approve', item }); setMotivo(''); };
  const openReject = item => { setPendingAction({ type: 'reject', item }); setMotivo(''); };
  const closeModal = () => !submitting && setPendingAction(null);

  const executeAction = async () => {
    if (!pendingAction) return;
    setSubmitting(true);
    try {
      const id = pendingAction.item?.id;
      if (pendingAction.type === 'approve') {
        await approveDowngrade(id, motivo);
      } else {
        await rejectDowngrade(id, motivo);
      }
      setPendingAction(null);
      setRefreshToken(t => t + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const headers = ['ID', 'Email', 'Plan anterior', 'Plan destino', 'Días en downgrade', 'Fecha creación', 'Estado', 'Acciones'];

  return (
    <>
      {loading ? (
        <Box mt={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box p={4}>
          <Box className={classes.filters}>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <InputLabel id="estado-label">Estado</InputLabel>
              <Select
                labelId="estado-label"
                value={filterEstado}
                onChange={handleFilterEstado}
                label="Estado"
              >
                {ESTADOS.map(e => (
                  <MenuItem key={e} value={e}>{e === 'all' ? 'Todos' : e}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              variant="outlined"
              size="small"
              label="Buscar por email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
            />
            <Button variant="outlined" size="small" onClick={handleEmailSearch}>
              Buscar
            </Button>
            {filterEmail && (
              <Button size="small" onClick={() => { setFilterEmail(''); setEmailInput(''); setPage(1); }}>
                Limpiar
              </Button>
            )}
          </Box>

          {rows.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table size="small" className={classes.table}>
                  <TableHead>
                    <TableRow className={classes.header}>
                      {headers.map(h => (
                        <TableCell key={h} align="center" className={classes.headerCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell align="center">{item.id}</TableCell>
                        <TableCell align="center">{item.email || '-'}</TableCell>
                        <TableCell align="center">{item.plan_anterior || '-'}</TableCell>
                        <TableCell align="center">{item.plan_a_asignar || '-'}</TableCell>
                        <TableCell align="center">{item.dias_en_downgrade ?? '-'}</TableCell>
                        <TableCell align="center">{formatDate(item.fecha_creacion)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.estado}
                            size="small"
                            style={{
                              background: estadoColor[item.estado] || '#757575',
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {item.estado === 'PENDIENTE' && (
                            <>
                              <IconButton
                                size="small"
                                title="Aprobar downgrade"
                                onClick={() => openApprove(item)}
                                disabled={submitting}
                                style={{ color: '#d32f2f' }}
                              >
                                <CheckCircleOutlineIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Rechazar downgrade"
                                onClick={() => openReject(item)}
                                disabled={submitting}
                                style={{ color: '#2e7d32' }}
                              >
                                <BlockIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className={classes.toolbar}>
                <FormControl variant="outlined" size="small" className={classes.formControl}>
                  <InputLabel id="page-size-label">Cantidad</InputLabel>
                  <Select
                    labelId="page-size-label"
                    value={pageSize}
                    onChange={handleChangePageSize}
                    label="Cantidad"
                  >
                    {pageSizeOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" className={classes.formControl} disabled={totalPages === 0}>
                  <InputLabel id="page-label">Página</InputLabel>
                  <Select
                    labelId="page-label"
                    value={Math.min(page, totalPages || 1)}
                    onChange={handleChangePage}
                    label="Página"
                  >
                    {pageOptions.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton onClick={handlePrev} disabled={page <= 1} aria-label="Anterior">
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton onClick={handleNext} disabled={page >= totalPages} aria-label="Siguiente">
                  <ChevronRightIcon />
                </IconButton>

                <Box className={classes.grow} />
                <Box>Página {page} de {totalPages || 1} — Total: {total}</Box>
              </Box>
            </>
          ) : (
            <Box className={classes.noResultsBox}>No hay resultados.</Box>
          )}
        </Box>
      )}

      <Dialog open={!!pendingAction} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pendingAction?.type === 'approve' ? 'Aprobar downgrade' : 'Rechazar downgrade'}
        </DialogTitle>
        <DialogContent dividers>
          {pendingAction?.type === 'approve'
            ? `Vas a aprobar el downgrade del cliente ${pendingAction?.item?.email} (solicitud #${pendingAction?.item?.id}). El cliente pasará al plan gratuito.`
            : `Vas a rechazar el downgrade del cliente ${pendingAction?.item?.email} (solicitud #${pendingAction?.item?.id}). El cliente conservará su plan actual.`}
          <TextField
            className={classes.motivoField}
            label="Motivo (opcional)"
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            size="small"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={submitting}>
            Volver
          </Button>
          <Button
            onClick={executeAction}
            variant="contained"
            disabled={submitting}
            style={{
              background: pendingAction?.type === 'approve' ? '#d32f2f' : '#2e7d32',
              color: '#fff',
            }}
          >
            {pendingAction?.type === 'approve' ? 'Aprobar' : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
